import React, { useState } from 'react';
import './review.css';

const questions = [
  {
    order: 4,
    fieldLabel: 'Email',
    fieldType: 'string',
    required: false,
    fieldAccess: 'editable',
    hideFor: [],
    dependsOn: '',
    options: [],
  },
  {
    order: 1,
    fieldLabel: 'Name',
    fieldType: 'string',
    required: true,
    fieldAccess: 'editable',
    hideFor: [],
    dependsOn: '',
    options: [],
  },
  {
    order: 6,
    fieldLabel: 'Checkbox Selector',
    fieldType: 'checkbox',
    required: false,
    fieldAccess: 'editable',
    hideFor: [],
    dependsOn: '',
    options: ['Checkbox1', 'Checkbox2', 'Checkbox3'],
  },
  {
    order: 2,
    fieldLabel: 'Date of Birth',
    fieldType: 'date',
    required: true,
    fieldAccess: 'editable',
    hideFor: [],
    dependsOn: '',
    options: [],
  },
  {
    order: 7,
    fieldLabel: 'Enter Complaint',
    fieldType: 'string',
    required: true,
    fieldAccess: 'editable',
    hideFor: [],
    dependsOn: '',
    options: [],
  },
  {
    order: 3,
    fieldLabel: 'Gender',
    fieldType: 'radio',
    required: false,
    fieldAccess: 'editable',
    hideFor: [],
    dependsOn: '',
    options: ['Male', 'Female', 'Other'],
  },
  {
    order: 5,
    fieldLabel: 'Select Complaint Type',
    fieldType: 'select',
    required: false,
    fieldAccess: 'editable',
    hideFor: [],
    dependsOn: '',
    options: ['Complaint', 'Feedback', 'Suggestion'],
  },
];

function fieldId(question) {
  return `review-${question.order}-${question.fieldLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}`;
}

function isLongText(question) {
  return question.fieldLabel.toLowerCase().includes('complaint');
}

export default function Review() {
  const [formData, setFormData] = useState({});
  const sortedQuestions = [...questions].sort((first, second) => first.order - second.order);

  const updateValue = (question, value) => {
    setFormData((currentData) => ({
      ...currentData,
      [question.fieldLabel]: value,
    }));
  };

  const renderField = (question) => {
    const id = fieldId(question);
    const value = formData[question.fieldLabel] || '';
    const commonProps = {
      id,
      name: question.fieldLabel,
      required: question.required,
      disabled: question.fieldAccess === 'read-only',
    };

    switch (question.fieldType) {
      case 'date':
        return (
          <input
            {...commonProps}
            className="review-control"
            type="date"
            value={value}
            onChange={(event) => updateValue(question, event.target.value)}
          />
        );
      case 'select':
      case 'dropdown':
        return (
          <select
            {...commonProps}
            className="review-control"
            value={value}
            onChange={(event) => updateValue(question, event.target.value)}
          >
            <option value="">Select {question.fieldLabel.toLowerCase()}</option>
            {question.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="review-options review-options--inline">
            {question.options.map((option, optionIndex) => {
              const optionId =
                optionIndex === 0
                  ? id
                  : `${id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
              return (
                <label className="review-option" htmlFor={optionId} key={option}>
                  <input
                    {...commonProps}
                    id={optionId}
                    type="radio"
                    value={option}
                    checked={value === option}
                    onChange={(event) => updateValue(question, event.target.value)}
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        );
      case 'checkbox':
        return (
          <div className="review-options review-options--inline">
            {question.options.map((option, optionIndex) => {
              const optionId =
                optionIndex === 0
                  ? id
                  : `${id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
              const selectedOptions = formData[question.fieldLabel] || [];
              return (
                <label className="review-option" htmlFor={optionId} key={option}>
                  <input
                    {...commonProps}
                    id={optionId}
                    name={`${question.fieldLabel}[]`}
                    type="checkbox"
                    value={option}
                    checked={selectedOptions.includes(option)}
                    onChange={(event) => {
                      const nextOptions = event.target.checked
                        ? [...selectedOptions, option]
                        : selectedOptions.filter((selectedOption) => selectedOption !== option);
                      updateValue(question, nextOptions);
                    }}
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        );
      case 'string':
      default:
        if (isLongText(question)) {
          return (
            <textarea
              {...commonProps}
              className="review-control review-control--textarea"
              value={value}
              placeholder="Enter your complaint here..."
              onChange={(event) => updateValue(question, event.target.value)}
            />
          );
        }
        return (
          <input
            {...commonProps}
            className="review-control"
            type="text"
            value={value}
            placeholder={`Enter ${question.fieldLabel.toLowerCase()}`}
            onChange={(event) => updateValue(question, event.target.value)}
          />
        );
    }
  };

  return (
    <div className="review-form" aria-label="Complaint review form">
      <div className="review-form-grid">
        {sortedQuestions.map((question) => (
          <div
            className={`review-question${isLongText(question) ? ' review-question--wide' : ''}`}
            key={fieldId(question)}
          >
            <label className="review-label" htmlFor={fieldId(question)}>
              {question.fieldLabel}
              {question.required ? <span className="review-required">*</span> : null}
            </label>
            {renderField(question)}
          </div>
        ))}
      </div>
    </div>
  );
}